import { z } from "zod";
import { createDescriptionSchema } from "./FormGroups/DescriptionFormGroup";
import { createDeadlineSchema } from "./FormGroups/DeadlineFormGroup";
import { createEstimateSchema } from "./FormGroups/EstimateFormGroup";
import { createContractSchema } from "./FormGroups/ContractFormGroup";
import { TFunction } from "i18next";
import { TenderType } from "@/graphql/__generated__/graphql";

/**
 * Creates a form schema for the tender details form
 * Handles the validation of public and private tender types here when it's not a quick offer
 * @param t - The translation function
 * @returns The form schema
 */
export const createDetailsFormSchema = (t: TFunction) => {
  return z.object({
    ...createDescriptionSchema(t).shape,
    ...createDeadlineSchema(t).shape,
    ...createEstimateSchema(t).shape,
    ...createContractSchema(t).shape,
  }).check((ctx) => {
    const tenderType = ctx.value.description.type;

    if (tenderType !== TenderType.QuickOffer) {
      // Description form group
      const { province, permanent, mobile, alarm } = ctx.value.description;

      if (!province) {
        ctx.issues.push({
          code: "custom",
          message: t("schemas:createtender.description.provincerequired"),
          path: ["description.province"],
          input: ctx.value.description.province,
        });
      }

      if (!permanent && !mobile && !alarm) {
        ctx.issues.push({
          code: "custom",
          message: t("schemas:createtender.description.servicetypemin"),
          path: ["description", "serviceType"],
          input: {
            permanent,
            mobile,
            alarm,
          },
        });
      }

      // Deadline form group
      const { publication, questions } = ctx.value.deadline;

      if (tenderType !== TenderType.Private) {
        // Conditional validation logic for public tenders
        if (!publication) {
          ctx.issues.push({
            code: "custom",
            message: t("schemas:createtender.publicpublicationdaterequired"),
            path: ["deadline", "publication"],
            input: ctx.value.deadline.publication,
          });
        }

        if (!questions) {
          ctx.issues.push({
            code: "custom",
            message: t("schemas:createtender.publicquestionsdaterequired"),
            path: ["deadline", "questions"],
            input: ctx.value.deadline.questions,
          });
        }
      }

      // Estimate form group
      const { revenue, hoursPerMonth, margin, effort } = ctx.value.estimate;

      if (!revenue) {
        ctx.issues.push({
          code: "custom",
          message: t("schemas:createtender.estimate.revenuerequired"),
          path: ["estimate.revenue"],
          input: ctx.value.estimate.revenue,
        });
      }

      if (!hoursPerMonth) {
        ctx.issues.push({
          code: "custom",
          message: t("schemas:createtender.estimate.hourspermonthrequired"),
          path: ["estimate.hoursPerMonth"],
          input: ctx.value.estimate.hoursPerMonth,
        });
      }

      if (!margin) {
        ctx.issues.push({
          code: "custom",
          message: t("schemas:createtender.estimate.marginrequired"),
          path: ["estimate.margin"],
          input: ctx.value.estimate.margin,
        });
      }

      if (!effort) {
        ctx.issues.push({
          code: "custom",
          message: t("schemas:form.required"),
          path: ["estimate.effort"],
          input: ctx.value.estimate.effort,
        });
      }

      // Contract form group
      const { priceCriteria, qualityCriteria, referenceCriteria } = ctx.value.contract;
      // sum of price, qualityConcept and references should be 100
      const sum = (priceCriteria ?? 0) + (qualityCriteria ?? 0) + (referenceCriteria ?? 0);
      if (sum !== 100) {
        ctx.issues.push({
          code: "custom",
          message: t("schemas:createtender.contract.sumofawardingcriteria"),
          path: ["contract.referenceCriteria"],
          input: ctx.value.contract,
        });
      }

    }
  });
};
